import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Animate, ViewContext } from 'components/lib';
import DateUtils from 'helpers/dateUtils';
import { FieldInput } from 'components/fieldInput';
import ResponsiveTable from 'components/ResponsiveTable';
import { policyColumns } from './columns';

const dateUtils = new DateUtils();

export function Policies() {
  const context = useContext(ViewContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  async function listPolicies(
    pageIndex,
    pageSize,
    search,
    statusFilter
  ) {
    if (loading) return;

    setLoading(true);

    await axios
      .get('/api/command-center/policies', {
        params: {
          page: pageIndex + 1,
          pageSize: pageSize,
          search,
          statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
        }
      })
      .then(async (res) => {
        setLoading(false);
        const responseData = res.data.data;

        const formattedData = responseData.data.policies.map((value) => {
          return {
            id: value.id,
            policy_number: value.policy_number,
            named_insured: value.named_insured,
            source: value.source,
            sent_at: dateUtils.formatIsoToCommomDate(value.sent_at),
            status: value.status,
          };
        });

        setData(formattedData);
        setPagination(prev => ({
          ...prev,
          totalItems: responseData.rows,
          totalPages: responseData.pages_count,
        }));
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        context.notification.show(
          'Failed to load table data',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  useEffect(() => {
    listPolicies(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      statusFilter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    statusFilter
  ]);

  const table = useReactTable({
    data,
    columns: policyColumns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    // Sort and Filter
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Define filters to pass to ResponsiveTable
  const tableFilters = (
    <div className="p-4 space-y-4">
      <FieldInput
        id="status_filter"
        type="multi_select"
        label="Filter by status"
        isDisabled={loading}
        defaultValue={[]}
        handleChange={(status) => {
          setStatusFilter(
            status.map((status) => {
              return status.value;
            })
          );
        }}
        options={[
          { value: 'active', label: 'Active' },
          { value: 'canceled', label: 'Canceled' },
          {
            value: 'pending_cancellation',
            label: 'Pending Cancellation',
          },
        ]}
      />
    </div>
  );

  return (
    <Animate type="pop">
      <ResponsiveTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        loading={loading}
        hasFooter={true}
        hasFilter={true}
        filters={tableFilters}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
      />
    </Animate>
  );
}
